import { get, patch, postFormData, postJson } from './apiClient';

export type AccountsPayableStatus =
  | 'unpaid'
  | 'partially_paid'
  | 'paid'
  | 'overdue'
  | 'on_hold'
  | 'disputed';

export interface AccountsPayableRecord {
  _id: string;
  vendorInvoiceId: string;
  supplierName: string;
  supplierContact?: string;
  vendorCreditDays?: number;
  invoiceNumber?: string;
  invoiceDate?: string | null;
  dueDate?: string | null;
  /**
   * Where dueDate came from. A due date is chased, so a calculated one must not
   * read as a figure the supplier printed. Server-authored — the patch endpoint
   * does not accept it; typing a date sets it to 'manual'.
   */
  dueDateSource?: 'printed' | 'terms' | 'vendor_terms' | 'manual' | null;
  currency?: string;
  totalAmount?: number;
  vatAmount?: number;
  paidAmount?: number;
  outstandingAmount?: number;
  /**
   * What the bill's APPLIED credits come to (a vendor return, a vendor credit balance). Sent by the
   * list only, worked out by the server's one owner of what is owed, so that
   * total - paid - credited = outstanding can be read off the row. Absent from other reads.
   */
  creditedAmount?: number;
  status: AccountsPayableStatus;
  accountingStatus?: 'not_sent' | 'accounting_team';
  approvalStatus?: 'not_required' | 'pending' | 'approved' | 'rejected';
  workflowNote?: string;
  disputeNote?: string;
  holdNote?: string;
  lineItems?: Array<Record<string, unknown>>;
  paymentReferences?: Array<{
    reference?: string;
    paymentDate?: string | null;
    amount?: number;
    method?: string;
    note?: string;
    /**
     * Server-authored (AP-0). True on a credit note that lowered outstanding.
     * A credit note recorded before AP-0 lacks it: it never moved the balance
     * and is not applied retroactively (D31).
     */
    appliedToBalance?: boolean;
    /** D44: the vendor return (`debit_note`) or balance (`vendor_credit`) this credit came from. */
    debitNoteId?: string;
    vendorCreditId?: string;
    /** Set when the credit was taken back; `appliedToBalance` is then false. */
    reversedAt?: string | null;
  }>;
  notes?: string;
  lastSyncedAt?: string | null;
  auditTrail?: Array<{
    action?: string;
    previousStatus?: string;
    nextStatus?: string;
    note?: string;
    actor?: string;
    at?: string;
  }>;
  source?: {
    kind?: string;
    fileName?: string;
    filePath?: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

/** The list's money, one entry per currency present, each in its own currency (never added across). */
export type SummaryByCurrency = Record<
  string,
  { invoiceCount?: number; totalAmount: number; paidAmount: number; outstandingAmount: number }
>;

export interface AccountsPayableListResponse {
  items: AccountsPayableRecord[];
  page: number;
  limit: number;
  total: number;
  pages: number;
  summary: {
    invoiceCount: number;
    totalAmount: number;
    paidAmount: number;
    outstandingAmount: number;
    unpaidCount: number;
    partialCount: number;
    paidCount: number;
    overdueCount: number;
    byCurrency?: SummaryByCurrency;
  };
}

export interface AccountsPayablePaymentRecord {
  _id: string;
  supplierName: string;
  reference: string;
  chequeNumber?: string;
  onlineReferenceNumber?: string;
  paymentDate?: string | null;
  amount: number;
  currency?: string;
  method?: string;
  note?: string;
  allocations?: Array<{
    accountsPayableId: string;
    invoiceNumber?: string;
    supplierName?: string;
    amount: number;
    note?: string;
  }>;
  unallocatedAmount?: number;
  status?: string;
  /** The payment run (D49 phase 4) this voucher was recorded from, when it was. */
  paymentRunId?: string;
  paymentRunNumber?: string;
  createdAt?: string;
  updatedAt?: string;
}

/**
 * The payment lists' money, one entry per currency present, each in its own currency: a TT$300 cheque and a
 * US$50 wire are never "350". The unlabelled top-level figures beside it are those groups added together
 * and mean one number only while one currency is present.
 */
export type PaymentTotalsByCurrency = Record<
  string,
  { paymentCount: number; totalAmount: number; allocatedAmount: number; unallocatedAmount: number }
>;

export interface AccountsPayablePaymentListResponse {
  items: AccountsPayablePaymentRecord[];
  page: number;
  limit: number;
  total: number;
  pages: number;
  summary: {
    paymentCount: number;
    totalAmount: number;
    allocatedAmount: number;
    unallocatedAmount: number;
    byCurrency?: PaymentTotalsByCurrency;
  };
}

/** The quick "due" chips of the bill list (D49 phase 2). Same words the server accepts. */
export type AccountsPayableDueChip = 'overdue' | 'due_today' | 'due_7' | 'due_30' | 'no_due_date';
export type AccountsPayableSort = 'dueDate' | 'invoiceDate' | 'outstanding' | 'totalAmount' | 'supplier';

/**
 * What the bill list can be narrowed and ordered by. Every filter is applied by the server (the
 * summary cards follow the same filter); dates are `yyyy-mm-dd`, amounts are the bill's own
 * currency. A blank value means "no limit" and is never sent.
 */
export interface ListAccountsPayablesParams {
  page?: number;
  limit?: number;
  status?: AccountsPayableStatus | 'all';
  supplier?: string;
  /** With `supplier`: the stored name exactly, not "contains" (a link from a report row). */
  supplierExact?: boolean;
  q?: string;
  dueFrom?: string;
  dueTo?: string;
  invoiceFrom?: string;
  invoiceTo?: string;
  minOutstanding?: string;
  maxOutstanding?: string;
  currency?: string;
  /** Only bills that still owe something. */
  openOnly?: boolean;
  /** `only` = on hold or disputed, `exclude` = everything else. */
  held?: 'only' | 'exclude';
  due?: AccountsPayableDueChip;
  sort?: AccountsPayableSort;
  dir?: 'asc' | 'desc';
}

export async function listAccountsPayables(params: ListAccountsPayablesParams) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 25));
  qs.set('status', String(params.status ?? 'all'));
  if (params.supplier) qs.set('supplier', params.supplier);
  // A blank exact supplier is "bills with no supplier name", so this rides along even when empty.
  if (params.supplierExact) {
    qs.set('supplierExact', 'true');
    if (!params.supplier) qs.set('supplier', '');
  }
  if (params.q) qs.set('q', params.q);
  const optional: Array<[string, string | undefined]> = [
    ['dueFrom', params.dueFrom],
    ['dueTo', params.dueTo],
    ['invoiceFrom', params.invoiceFrom],
    ['invoiceTo', params.invoiceTo],
    ['minOutstanding', params.minOutstanding],
    ['maxOutstanding', params.maxOutstanding],
    ['currency', params.currency],
    ['held', params.held],
    ['due', params.due],
    ['sort', params.sort],
    ['dir', params.dir],
  ];
  for (const [key, value] of optional) {
    if (value !== undefined && String(value).trim() !== '') qs.set(key, String(value).trim());
  }
  if (params.openOnly) qs.set('openOnly', 'true');
  return get<{ success: boolean; data: AccountsPayableListResponse }>(`/accounts-payable?${qs.toString()}`);
}

export async function syncAccountsPayables(force = false) {
  // `notConfirmed`: uploads that got no bill because nobody has confirmed them yet.
  // `duplicates` / `duplicateInvoiceIds`: confirmed invoices that got no bill because another invoice of
  // the same supplier and number already has one (D116.3). Both Accounts pages say so after Sync
  // (`syncDuplicatesNote`).
  return postJson<{
    success: boolean;
    data: SyncAccountsPayablesResult;
  }>(
    '/accounts-payable/sync',
    { force }
  );
}

export interface SyncAccountsPayablesResult {
  created: number;
  updated: number;
  notConfirmed: number;
  duplicates: number;
  duplicateInvoiceIds: string[];
  total: number;
}

/** The one line a Sync that skipped duplicates owes the person who pressed it; null when it skipped none. */
export function syncDuplicatesNote(result?: Pick<SyncAccountsPayablesResult, 'duplicates'> | null): string | null {
  const count = Number(result?.duplicates);
  if (!(count > 0)) return null;
  return `${count} confirmed invoice${count === 1 ? ' has' : 's have'} no bill (duplicate of an existing bill)`;
}

export async function patchAccountsPayable(
  id: string,
  body: Record<string, unknown>
) {
  return patch<{ success: boolean; data: { ok: boolean; matched: number; modified: number } }>(
    `/accounts-payable/${id}`,
    body
  );
}

/**
 * Is this payment reference a CREDIT rather than a payment? For display only —
 * what a bill still owes is always the server's figure. The methods are the
 * ones utils/payableBalance.js `CREDIT_METHODS` counts: a credit note typed
 * before D44, an approved vendor return, and a vendor credit balance.
 * ("Record Credit Note" was removed by D44, 2026-09-17: a supplier credit is
 * now a vendor return, lib/debitNoteApi.ts.)
 */
export function isCreditReference(ref: { method?: string } | null | undefined): boolean {
  return ref?.method === 'credit_note' || ref?.method === 'debit_note' || ref?.method === 'vendor_credit';
}

export async function getAccountsPayableById(id: string) {
  return get<{ success: boolean; data: AccountsPayableRecord }>(`/accounts-payable/${id}`);
}

export async function updateAccountsPayableWorkflow(
  id: string,
  body: {
    approvalStatus?: 'not_required' | 'pending' | 'approved' | 'rejected';
    status?: AccountsPayableStatus;
    note?: string;
  }
) {
  return postJson<{ success: boolean; data: AccountsPayableRecord }>(`/accounts-payable/${id}/workflow`, body);
}

export async function listAccountsPayablePayments(params: {
  page?: number;
  limit?: number;
  supplier?: string;
  q?: string;
  status?: string;
  method?: string;
}) {
  const qs = new URLSearchParams();
  qs.set('page', String(params.page ?? 1));
  qs.set('limit', String(params.limit ?? 25));
  if (params.supplier) qs.set('supplier', params.supplier);
  if (params.q) qs.set('q', params.q);
  if (params.status) qs.set('status', params.status);
  if (params.method) qs.set('method', params.method);
  return get<{ success: boolean; data: AccountsPayablePaymentListResponse }>(
    `/accounts-payable/payments?${qs.toString()}`
  );
}

// Recording a payment lives in lib/accountsPayablePaymentsApi.ts (`createPaymentVoucher`, D49 phase 5),
// which also carries the "hold the excess as a supplier credit" fields. It used to be here too.

export async function getAccountsPayablePaymentById(id: string) {
  return get<{ success: boolean; data: AccountsPayablePaymentRecord & {
    allocations?: Array<{
      accountsPayableId: string;
      invoiceNumber?: string;
      supplierName?: string;
      amount: number;
      note?: string;
      invoiceDate?: string | null;
      dueDate?: string | null;
      totalAmount?: number;
      outstandingBeforePayment?: number;
      currency?: string;
    }>;
    chequeNumber?: string;
    onlineReferenceNumber?: string;
  } }>(`/accounts-payable/payments/${id}`);
}

export async function importAccountsPayablePayments(file: File) {
  const formData = new FormData();
  formData.append('file', file);
  return postFormData<{
    success: boolean;
    data: {
      createdCount: number;
      errors: string[];
      fileName?: string;
    };
  }>('/accounts-payable/payments/import', formData);
}
